import moment from 'moment';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import {
	CompanyDeleteFileDTO,
	CompanyInfoDTO,
	CorporateBorrowerCompanyInfoDTO,
	CorporateBorrowerContactPersonDTO,
	CorporateBorrowerOnboardingProgressDTO,
	CorporateBorrowerOtherDetailDTO,
	CorporateBorrowerSaveAsDraftDTO,
	CorporateBorrowerSubmitDTO,
	CorporateContactPersonDTO,
	CorporateFunderOnboardingFsaDTO,
	CorporateFunderOnboardingProgressDTO,
	CorporateFunderSignFsaDTO,
	CorporateLoanLendingDTO,
	CorporateSaveDraftDTO,
	CorporateSubmitDTO,
	CreateFunderContactPersonResponse,
	ICorporateaFunderFSA
} from 'src/dto/corporate.onboarding.dto';
import {
	IBankAccount,
	IDocument,
	IFunderSupportingDocument,
	IBorrowerCreditHistory,
	IQuestionBorrowerOther,
	IAddress,
	IEmploymentDetail
} from 'src/interfaces/accounts/account';
import {
	ICompanyInfo,
	ICompanyRevenue,
	IContactPerson,
	ICorporateBorrowerOnboardingProgress,
	ICorporateFunderOnboardingProgress,
	ICorporateLevelDocuments,
	ICorporateLoanLending
} from 'src/interfaces/accounts/company';
import { IHttpService } from 'src/services/http.service';
import { ResponseDTO } from 'src/dto/base.dto';
import { ENDPOINT, HTTP_STATUS_RESPONSE_KEY } from 'src/constants/api';
import {
	CHINA_NATIONALITY_VALUE,
	COMPANY_ONBOARDING_TYPE,
	IMonthsOption,
	MONTH_OPTIONS,
	ONBOARDING_CURRENCY_TYPE,
	ONBOARDING_STATUSES,
	ONBOARDING_TYPE
} from 'src/constants';
import { CorporateBankAccountDTO, DeleteFileDTO, ICreateFunderCorporateRequest } from 'src/dto/onboarding.dto';
import { IOnboardingStore } from '../user/onboarding.store';
import { convertNumberToStringForObject, convertStringToNumberForObject } from 'src/helpers/common.utils';
import { IUserStore } from '../user';

export const ICompanyInfoCols: Array<keyof ICompanyInfo> = ['annualSales', 'latestNetProfit'];
export const ICorporateLoanLendingCols: Array<keyof ICorporateLoanLending> = [
	'totalCommittedLoanAmount',
	'maxLoanPerDeal'
];
export const ICompanyRevenueCols: Array<keyof ICompanyRevenue> = ['revenue'];

interface IFunderStepValidattion {
	loanLendingCriteria?: boolean;
	bankAccount?: boolean;
}

interface IBorrowerStepValidattion {
	companyDetail?: boolean;
	onboardingOtherDetail?: boolean;
}
export interface ICorporateOnboardingStore {
	onboardingType: ONBOARDING_TYPE;
	onboardingStatus: ONBOARDING_STATUSES;
	onboardingFSA?: ICorporateaFunderFSA;
	onboardingCurrency?: ONBOARDING_CURRENCY_TYPE;
	defaultOnboardingCurrency?: ONBOARDING_CURRENCY_TYPE;
	companyDetail?: ICompanyInfo;
	defaultCompanyDetail?: ICompanyInfo;
	contractPerson?: IContactPerson;
	companyOnboardingType?: COMPANY_ONBOARDING_TYPE;
	loanLendingCriteria?: ICorporateLoanLending;
	defaultLoanLendingCriteria?: ICorporateLoanLending;
	bankAccount?: IBankAccount;
	supportingDocuments?: IFunderSupportingDocument;
	corporateLevelDocuments?: ICorporateLevelDocuments;
	onboardingOtherDetail?: IBorrowerCreditHistory;
	defaultOtherDetails?: IBorrowerCreditHistory;

	submittedToKyc?: boolean;
	funderStepValidattion: IFunderStepValidattion;
	borrowerStepValidation: IBorrowerStepValidattion;

	updateLoanLendingCriteria(newData: ICorporateLoanLending): void;
	setOnboardingType(type: COMPANY_ONBOARDING_TYPE): void;

	saveCompanyDetail(body: ICompanyInfo): Promise<boolean>;
	updateCompanyDetail(newData: ICompanyInfo): void;
	saveContactPerson(body: IContactPerson, obType: ONBOARDING_TYPE): Promise<boolean>;
	saveLoanLendingCriteria(body: ICorporateLoanLending): Promise<boolean>;
	saveBankAccount(body: IBankAccount): Promise<boolean>;

	saveBorrowerCompanyDetail(body: ICompanyInfo): Promise<boolean>;
	saveBorrowerContactPerson(body: IContactPerson): Promise<boolean>;
	saveBorrowerOtherDetail(body: IBorrowerCreditHistory): Promise<boolean>;

	saveDraft(): Promise<boolean>;
	submit(): Promise<boolean>;
	loadProgress(type?: COMPANY_ONBOARDING_TYPE): Promise<void>;
	uploadFile(form: FormData): Promise<boolean>;
	uploadCompanyFile(form: FormData): Promise<boolean>;
	downloadFile(type: keyof IFunderSupportingDocument, id?: string): Promise<void>;
	downloadCompanyFile(type: keyof ICorporateLevelDocuments, id?: string): Promise<void>;
	deleteFile(id?: string): Promise<boolean>;
	deleteCompanyFileFile(id?: string): Promise<boolean>;

	saveBorrowerDraft(): Promise<boolean>;
	submitBorrower(): Promise<boolean>;
	getFunderFSA(): Promise<void>;
	signFSA(): Promise<boolean>;
	submitFsaFile(formData: FormData): Promise<boolean>;

	setFunderStepValidation(step: IFunderStepValidattion): void;
	setBorrowerStepValidation(step: IBorrowerStepValidattion): void;

	dispose(): void;

	updateMinimumLoanPeriod(value: number): void;

	get monthsMaximumOption(): IMonthsOption[];

	clearOtherInput(type: string): void;

	updateMaritalStatus(value: string): void;

	updateSourceOfFunds(value: string[]): void;

	updateContactPerson(newContactPerson: IContactPerson): void;

	updateQuestionOtherDetails(value: IQuestionBorrowerOther): void;

	disableButtonSubmit(step: number): boolean;

	get disabledSubmitBtn(): boolean;
	get isChineseNationality(): boolean;

	get isAllStepComplete(): boolean;

	updatePropertyCountry(value: string[]): void;
	setOnboardingCurrency(currency: ONBOARDING_CURRENCY_TYPE): void;
}

export class CorporateOnboardingStore implements ICorporateOnboardingStore {
	onboardingType: ONBOARDING_TYPE = ONBOARDING_TYPE.FUNDER;
	onboardingStatus: ONBOARDING_STATUSES = ONBOARDING_STATUSES.InCompleted;
	onboardingCurrency?: ONBOARDING_CURRENCY_TYPE = ONBOARDING_CURRENCY_TYPE.HKD;
	defaultOnboardingCurrency?: ONBOARDING_CURRENCY_TYPE = ONBOARDING_CURRENCY_TYPE.HKD;
	onboardingFSA?: ICorporateaFunderFSA;
	companyDetail?: ICompanyInfo;
	defaultCompanyDetail?: ICompanyInfo;
	contractPerson?: IContactPerson;
	loanLendingCriteria?: ICorporateLoanLending;
	defaultLoanLendingCriteria?: ICorporateLoanLending;
	bankAccount?: IBankAccount;
	supportingDocuments?: IFunderSupportingDocument;
	corporateLevelDocuments?: ICorporateLevelDocuments;
	companyOnboardingType = COMPANY_ONBOARDING_TYPE.FUNDER;
	onboardingOtherDetail?: IBorrowerCreditHistory = {};
	defaultOtherDetails?: IBorrowerCreditHistory = {};

	submittedToKyc?: boolean = false;
	funderStepValidattion: IFunderStepValidattion = {
		loanLendingCriteria: false,
		bankAccount: false
	};
	borrowerStepValidation: IBorrowerStepValidattion = {
		companyDetail: false,
		onboardingOtherDetail: false
	}

	constructor(protected readonly http: IHttpService, private onboardingStore: IOnboardingStore, private userStore: IUserStore) {
		makeObservable(this, {
			onboardingCurrency: observable,
			onboardingStatus: observable,
			companyOnboardingType: observable,
			companyDetail: observable,
			contractPerson: observable,
			loanLendingCriteria: observable.deep,
			defaultLoanLendingCriteria: observable.deep,
			bankAccount: observable.deep,
			supportingDocuments: observable.deep,
			corporateLevelDocuments: observable.deep,
			loadProgress: action.bound,
			saveContactPerson: action,
			saveLoanLendingCriteria: action,
			setOnboardingType: action,
			monthsMaximumOption: computed,
			updateMinimumLoanPeriod: action.bound,
			clearOtherInput: action.bound,
			updateMaritalStatus: action.bound,
			updateSourceOfFunds: action.bound,
			updateContactPerson: action.bound,
			onboardingOtherDetail: observable.shallow,
			defaultOtherDetails: observable.shallow,
			updatePropertyCountry: action.bound,
			disabledSubmitBtn: computed,
			isChineseNationality: computed,
			updateCompanyDetail: action.bound,
			updateLoanLendingCriteria: action.bound,
			setOnboardingCurrency: action.bound,
			setFunderStepValidation: action.bound,
			setBorrowerStepValidation: action.bound,

			funderStepValidattion: observable.deep,
			borrowerStepValidation: observable.deep,
			isAllStepComplete: computed
		});
	}

	public async saveLoanLendingCriteria(body: ICorporateLoanLending): Promise<boolean> {
		body = convertStringToNumberForObject<ICorporateLoanLending>(body, ICorporateLoanLendingCols);
		if (this.loanLendingCriteria?.id) body.id = this.loanLendingCriteria.id;
		const dto = new CorporateLoanLendingDTO({
			...body,
			personId: this.contractPerson?.personId
		});
		const result: ResponseDTO<string> = await this.http.request<CorporateLoanLendingDTO, string>(dto);
		if (result.responseKey == HTTP_STATUS_RESPONSE_KEY.SUCCESS) {
			runInAction(() => {
				this.loanLendingCriteria = {
					...convertNumberToStringForObject<ICorporateLoanLending>(body, ICorporateLoanLendingCols),
					id: result.data
				};

				this.defaultLoanLendingCriteria = { ...this.loanLendingCriteria };
				this.setOnboardingCurrency(body.currency as ONBOARDING_CURRENCY_TYPE);
				this.defaultOnboardingCurrency = body.currency as ONBOARDING_CURRENCY_TYPE;
			});
			return true;
		}
		return false;
	}

	public updateLoanLendingCriteria(newData: ICorporateLoanLending): void {
		this.loanLendingCriteria = {
			...this.loanLendingCriteria,
			...newData
		};
	}

	public setOnboardingCurrency(currency: ONBOARDING_CURRENCY_TYPE): void {
		this.onboardingCurrency = currency;
	}

	public async saveContactPerson(body: IContactPerson): Promise<boolean> {
		const bodyDto: ICreateFunderCorporateRequest = {
			...body,
			personId: this.contractPerson?.personId,
			companyInfoId: this.companyDetail?.id,
			dateOfBirth: moment(new Date()).toDate(),
			personalAccountType:
				this.companyOnboardingType === COMPANY_ONBOARDING_TYPE.FUNDER
					? ONBOARDING_TYPE.FUNDER
					: ONBOARDING_TYPE.BORROWER,
			residentialAddress: {
				...body.residentialAddress,
				livedTime: Number(body.residentialAddress?.livedTime)
			} as IAddress,
			mailingAddress: body.mailingAddress as IAddress,
			otherMaritalStatus: this.contractPerson?.maritalStatus === 'other' ? body.otherMaritalStatus : undefined
		};

		if (this.contractPerson?.residentialAddress?.id) {
			bodyDto.residentialAddress.id = this.contractPerson?.residentialAddress.id;
		}
		if (bodyDto.mailingAddress && this.contractPerson?.mailingAddress?.id) {
			bodyDto.mailingAddress.id = this.contractPerson?.mailingAddress.id;
		}

		const dto = new CorporateContactPersonDTO(bodyDto);
		const result: ResponseDTO<CreateFunderContactPersonResponse> = await this.http.request<
			CorporateContactPersonDTO,
			CreateFunderContactPersonResponse
		>(dto);
		if (result.responseKey == HTTP_STATUS_RESPONSE_KEY.SUCCESS) {
			runInAction(() => {
				this.contractPerson = {
					...this.contractPerson,
					...body,
					personId: result.data?.personId,
					residentialAddress: {
						...body.residentialAddress,
						id: result.data?.residentialAddressId
					},
					id: result.data?.personId
				};

				if (result.data?.mailingAddressId) {
					this.contractPerson = {
						...this.contractPerson,
						mailingAddress: {
							...body.mailingAddress,
							id: result.data.mailingAddressId
						}
					};
				}
			});
			return true;
		}
		return false;
	}

	async saveCompanyDetail(body: ICompanyInfo): Promise<boolean> {
		body = convertStringToNumberForObject<ICompanyInfo>(body, ICompanyInfoCols);
		if (body.contactPerson && body.contactPerson?.employmentDetail) {
			body.contactPerson.employmentDetail = convertStringToNumberForObject<IEmploymentDetail>(
				body.contactPerson.employmentDetail,
				['annualSalary']
			);
			body.contactPerson.dateOfBirth = moment(body.contactPerson.dob).toDate();
		}
		const requestBody: ICompanyInfo = {
			...body,
			id: this.companyDetail?.id
		};

		if (moment.isMoment(body.dateOfIncorporation)) {
			requestBody.dateOfIncorporation = body.dateOfIncorporation.toDate();
		}
		const requestDTO = new CompanyInfoDTO({
			...requestBody
		});

		const result: ResponseDTO<ICompanyInfo> = await this.http.request<CompanyInfoDTO, ICompanyInfo>(requestDTO);
		if (result.responseKey == HTTP_STATUS_RESPONSE_KEY.SUCCESS) {
			runInAction(() => {
				this.companyDetail = {
					...this.companyDetail,
					...convertNumberToStringForObject(body, ICompanyInfoCols),
					id: result.data?.id
				};
				this.defaultCompanyDetail = {
					...this.companyDetail
				}
				this.contractPerson = {
					...this.contractPerson,
					id: result.data?.personId
				};
			});
			return true;
		}
		return false;
	}

	public updateCompanyDetail(newData: ICompanyInfo): void {
		this.companyDetail = {
			...this.companyDetail,
			...newData
		}
	}

	async saveBankAccount(body: IBankAccount): Promise<boolean> {
		const dto = new CorporateBankAccountDTO({
			...body,
			personId: this.contractPerson?.personId
		});
		const result: ResponseDTO<string> = await this.http.request<CorporateBankAccountDTO, string>(dto);
		if (result.responseKey == HTTP_STATUS_RESPONSE_KEY.SUCCESS) {
			runInAction(() => {
				this.bankAccount = {
					...body,
					id: result.data
				};
			});
			return true;
		}
		return false;
	}

	public async loadProgress(type = undefined): Promise<void> {
		if (type) {
			if (type == COMPANY_ONBOARDING_TYPE.FUNDER) {
				await this.loadFunderProgress();
			} else {
				await this.loadCorporateProgress();
			}
		} else if (this.companyOnboardingType === COMPANY_ONBOARDING_TYPE.FUNDER) {
			await this.loadFunderProgress();
		} else {
			await this.loadCorporateProgress();
		}
	}
	public async uploadFile(form: FormData): Promise<boolean> {
		form.append('onboardingType', COMPANY_ONBOARDING_TYPE.FUNDER.toString());
		const res: ResponseDTO<IFunderSupportingDocument> = await this.http.uploadFile(
			ENDPOINT.FUNDER_ONBOARDING_FILE_UPLOAD,
			form
		);
		if (res.responseKey === HTTP_STATUS_RESPONSE_KEY.SUCCESS) {
			runInAction(() => {
				this.supportingDocuments = {
					...res.data
				};
			});
			return true;
		}
		return false;
	}

	public async uploadCompanyFile(form: FormData): Promise<boolean> {
		form.append('personId', this.contractPerson?.personId || '');
		form.append('companyId', this.companyDetail?.id || '');
		form.append('onboardingType', this.companyOnboardingType.toString() || '');
		const res: ResponseDTO<ICorporateLevelDocuments> = await this.http.uploadFile(
			ENDPOINT.CORPORATE_FUNDER_FILE_UPLOAD,
			form
		);
		if (res.responseKey === HTTP_STATUS_RESPONSE_KEY.SUCCESS) {
			runInAction(() => {
				this.corporateLevelDocuments = {
					...res.data
				};
			});
			return true;
		}
		return false;
	}

	async downloadFile(type: keyof IFunderSupportingDocument, id?: string | undefined): Promise<void> {
		if (!id || !this.supportingDocuments || !this.supportingDocuments[type]) {
			return;
		}
		const documents: IDocument | IDocument[] | undefined = this.supportingDocuments[type];
		let doc: IDocument | undefined;

		if (!documents) {
			return;
		}
		if (Array.isArray(documents)) {
			doc = documents.find((d) => d.id == id);
		} else {
			doc = documents;
		}
		if (!doc) {
			return;
		}
		await this.http.downloadFile(
			ENDPOINT.FUNDER_FILE_DOWNLOAD.concat(id),
			{},
			{
				name: doc.fileName || ''
			}
		);
	}

	async downloadCompanyFile(type: keyof ICorporateLevelDocuments, id?: string | undefined): Promise<void> {
		if (!id || !this.corporateLevelDocuments || !this.corporateLevelDocuments[type]) {
			return;
		}
		const documents: IDocument | IDocument[] | undefined = this.corporateLevelDocuments[type];
		let doc: IDocument | undefined;

		if (!documents) {
			return;
		}
		if (Array.isArray(documents)) {
			doc = documents.find((d) => d.id == id);
		} else {
			doc = documents;
		}
		if (!doc) {
			return;
		}
		await this.http.downloadFile(
			ENDPOINT.CORPORATE_FUNDER_FILE_DOWNLOAD.concat(id),
			{},
			{
				name: doc.fileName || ''
			}
		);
	}

	public async deleteFile(id?: string | undefined): Promise<boolean> {
		if (!id) {
			return true;
		}
		const dto = new DeleteFileDTO({
			documentId: id
		});

		const res: ResponseDTO<IFunderSupportingDocument> = await this.http.request<
			DeleteFileDTO,
			IFunderSupportingDocument
		>(dto);
		if (res.responseKey === HTTP_STATUS_RESPONSE_KEY.SUCCESS) {
			runInAction(() => {
				this.supportingDocuments = {
					...res.data
				};
			});
			return true;
		}
		return false;
	}

	async deleteCompanyFileFile(id?: string): Promise<boolean> {
		if (!id) {
			return true;
		}
		const dto = new CompanyDeleteFileDTO({
			documentId: id
		});

		const res: ResponseDTO<ICorporateLevelDocuments> = await this.http.request<
			CompanyDeleteFileDTO,
			ICorporateLevelDocuments
		>(dto);
		if (res.responseKey === HTTP_STATUS_RESPONSE_KEY.SUCCESS) {
			runInAction(() => {
				this.corporateLevelDocuments = {
					...res.data
				};
			});
			return true;
		}
		return false;
	}

	async saveDraft(): Promise<boolean> {
		const dto = new CorporateSaveDraftDTO();
		const result: ResponseDTO<string> = await this.http.request<CorporateSaveDraftDTO, string>(dto);
		if (result.responseKey == HTTP_STATUS_RESPONSE_KEY.SUCCESS) {
			runInAction(() => {
				this.onboardingStatus = ONBOARDING_STATUSES.Draft;
			});
			return true;
		}
		return false;
	}

	async submit(): Promise<boolean> {
		const dto = new CorporateSubmitDTO();
		const result: ResponseDTO<string> = await this.http.request<CorporateSubmitDTO, string>(dto);
		if (result.responseKey == HTTP_STATUS_RESPONSE_KEY.SUCCESS) {
			runInAction(() => {
				this.onboardingStatus = ONBOARDING_STATUSES.WaitingForApproval;
			});
			return true;
		}
		return false;
	}

	private async loadFunderProgress(): Promise<void> {
		const dto = new CorporateFunderOnboardingProgressDTO();
		const result: ResponseDTO<ICorporateFunderOnboardingProgress> = await this.http.request<
			CorporateFunderOnboardingProgressDTO,
			ICorporateFunderOnboardingProgress
		>(dto);
		if (result.responseKey === HTTP_STATUS_RESPONSE_KEY.SUCCESS) {
			runInAction(() => {
				this.onboardingType = ONBOARDING_TYPE.FUNDER;
				this.onboardingStatus = ONBOARDING_STATUSES.Draft;
				const data = result.data || ({} as ICorporateFunderOnboardingProgress);
				this.companyDetail = {
					...convertNumberToStringForObject<ICompanyInfo>(data.companyOnboardingDetail || {}, ICompanyInfoCols)
				};
				this.defaultCompanyDetail = { ...this.companyDetail }
				if (this.companyDetail && this.companyDetail.dateOfIncorporation) {
					this.companyDetail.dateOfIncorporation = moment(data.companyOnboardingDetail?.dateOfIncorporation);
				}
				this.contractPerson = {
					...data.contractPerson,
					companyId: data.companyOnboardingDetail?.id,
					residentialAddress: {
						id: data.contractPerson?.addressId,
						addressLine: data.contractPerson?.addressLine,
						street: data.contractPerson?.street,
						country: data.contractPerson?.country,
						district: data.contractPerson?.district,
						zipCode: data.contractPerson?.zipCode,
						livedTime: data.contractPerson?.livedTime
					},
					mailingAddress: {
						id: data.contractPerson?.mailingAddressId,
						addressLine: data.contractPerson?.mailingAddressLine,
						street: data.contractPerson?.mailingStreet,
						country: data.contractPerson?.mailingCountry,
						district: data.contractPerson?.mailingDistrict,
						zipCode: data.contractPerson?.mailingZipCode
					},
					dob: moment(data.contractPerson?.dob).unix() > 0 ? moment(data.contractPerson?.dob) : undefined
				};
				if (this.contractPerson?.dob) {
					this.contractPerson.dob = moment(this.contractPerson.dob);
				}
				this.loanLendingCriteria = {
					...convertNumberToStringForObject<ICorporateLoanLending>(
						data.loanLendingCriteriaOnboarding || {},
						ICorporateLoanLendingCols
					)
				};
				this.onboardingCurrency = this.defaultOnboardingCurrency = data.referenceCurrency as ONBOARDING_CURRENCY_TYPE;

				this.defaultLoanLendingCriteria = { ...this.loanLendingCriteria };
				this.bankAccount = data.bankAccountOnboarding;
				this.supportingDocuments = {
					...data.supportingDocuments
				};

				this.corporateLevelDocuments = {
					...data.corporateLevelDocuments
				};

				this.submittedToKyc = data.submittedToKyc;

				if (data.contractPerson?.personId) {
					this.userStore.setPersonId(data.contractPerson?.personId)
				}
			});
		}
	}

	private async loadCorporateProgress(): Promise<void> {
		const dto = new CorporateBorrowerOnboardingProgressDTO();
		const result: ResponseDTO<ICorporateBorrowerOnboardingProgress> = await this.http.request<
			CorporateBorrowerOnboardingProgressDTO,
			ICorporateBorrowerOnboardingProgress
		>(dto);
		if (result.responseKey === HTTP_STATUS_RESPONSE_KEY.SUCCESS) {
			runInAction(() => {
				this.onboardingType = ONBOARDING_TYPE.BORROWER;
				this.onboardingStatus = ONBOARDING_STATUSES.Draft;
				const data = result.data || ({} as ICorporateBorrowerOnboardingProgress);
				this.companyDetail = {
					...data.companyDetail,
					referenceCurrency: result.data?.referenceCurrency
				};
				if (this.companyDetail && this.companyDetail.netProfits && this.companyDetail.netProfits.length) {
					this.companyDetail.netProfits = this.companyDetail.netProfits.map((np) => {
						return convertNumberToStringForObject<ICompanyRevenue>(np, ICompanyRevenueCols);
					});
				}
				if (this.companyDetail && this.companyDetail.dateOfIncorporation) {
					this.companyDetail.dateOfIncorporation = moment(data.companyDetail?.dateOfIncorporation);
				}


				this.defaultCompanyDetail = { ...this.companyDetail }
				this.contractPerson = {
					...data.contractPerson,
					companyId: data.companyDetail?.id,
					dob: moment(data.contractPerson?.dob).unix() > 0 ? moment(data.contractPerson?.dob) : undefined
				};

				this.onboardingOtherDetail = {
					...data.otherDetails
				};

				if (data.otherDetails && data.otherDetails.questions) {
					this.onboardingOtherDetail.questions = JSON.parse(data.otherDetails.questions as string);
				}

				this.defaultOtherDetails = {
					...this.onboardingOtherDetail
				}
				this.corporateLevelDocuments = {
					...data.corporateLevelDocuments
				};
				this.submittedToKyc = data.submittedToKyc;

				this.setOnboardingCurrency(data.referenceCurrency as ONBOARDING_CURRENCY_TYPE);
				this.defaultOnboardingCurrency = data.referenceCurrency as ONBOARDING_CURRENCY_TYPE;

				if (data.contractPerson?.personId) {
					this.userStore.setPersonId(data.contractPerson?.personId)
				}
			});
		}
	}

	public async saveBorrowerCompanyDetail(body: ICompanyInfo): Promise<boolean> {
		const dto = new CorporateBorrowerCompanyInfoDTO({
			...body,
			id: this.companyDetail?.id
		});
		if (body.netProfits && body.netProfits.length) {
			const netProfits = body.netProfits.map((np) => {
				return convertStringToNumberForObject<ICompanyRevenue>(np, ICompanyRevenueCols);
			});
			dto.body.netProfits = netProfits;
		}
		const rs = await this.http.request<CorporateBorrowerCompanyInfoDTO, string>(dto);
		if (rs.responseKey === HTTP_STATUS_RESPONSE_KEY.SUCCESS) {
			runInAction(() => {
				this.companyDetail = {
					...this.companyDetail,
					...body,
					id: rs.data
				};
			});
			return true;
		}
		return false;
	}

	public async saveBorrowerContactPerson(body: IContactPerson): Promise<boolean> {
		const dto = new CorporateBorrowerContactPersonDTO({
			...body,
			companyId: this.companyDetail?.id
		});
		const rs = await this.http.request<CorporateBorrowerContactPersonDTO, string>(dto);
		if (rs.responseKey === HTTP_STATUS_RESPONSE_KEY.SUCCESS) {
			runInAction(() => {
				this.contractPerson = {
					...this.contractPerson,
					...body,
					personId: rs.data
				};
			});
			return true;
		}
		return false;
	}

	public async saveBorrowerOtherDetail(body: IBorrowerCreditHistory): Promise<boolean> {
		const dto = new CorporateBorrowerOtherDetailDTO({
			...body,
			personId: this.contractPerson?.personId,
			questions: JSON.stringify(body.questions)
		});
		const rs = await this.http.request<CorporateBorrowerOtherDetailDTO, string>(dto);
		if (rs.responseKey === HTTP_STATUS_RESPONSE_KEY.SUCCESS) {
			runInAction(() => {
				this.onboardingOtherDetail = {
					...this.onboardingOtherDetail,
					...body,
					id: rs.data
				};

				this.defaultOtherDetails = { ...this.onboardingOtherDetail }
			});
			return true;
		}
		return false;
	}

	public async saveBorrowerDraft(): Promise<boolean> {
		const dto = new CorporateBorrowerSaveAsDraftDTO();
		const result: ResponseDTO<string> = await this.http.request<CorporateBorrowerSaveAsDraftDTO, string>(dto);
		if (result.responseKey == HTTP_STATUS_RESPONSE_KEY.SUCCESS) {
			runInAction(() => {
				this.onboardingStatus = ONBOARDING_STATUSES.Draft;
			});
			return true;
		}
		return false;
	}

	public async submitBorrower(): Promise<boolean> {
		const dto = new CorporateBorrowerSubmitDTO();
		const result: ResponseDTO<string> = await this.http.request<CorporateBorrowerSubmitDTO, string>(dto);
		if (result.responseKey == HTTP_STATUS_RESPONSE_KEY.SUCCESS) {
			runInAction(() => {
				this.onboardingStatus = ONBOARDING_STATUSES.WaitingForApproval;
			});
			return true;
		}
		return false;
	}

	public setFunderStepValidation(step: IFunderStepValidattion): void {
		this.funderStepValidattion = step;
	}

	public setBorrowerStepValidation(step: IBorrowerStepValidattion): void {
		this.borrowerStepValidation = step;
	}

	public setOnboardingType(type: COMPANY_ONBOARDING_TYPE): void {
		this.companyOnboardingType = type;
	}

	public async getFunderFSA(): Promise<void> {
		const FunderFSADto = new CorporateFunderOnboardingFsaDTO();
		const result: ResponseDTO<ICorporateaFunderFSA> = await this.http.request<
			CorporateFunderOnboardingFsaDTO,
			ICorporateaFunderFSA
		>(FunderFSADto);
		if (result.responseKey === HTTP_STATUS_RESPONSE_KEY.SUCCESS) {
			runInAction(() => {
				this.onboardingFSA = result.data as ICorporateaFunderFSA;
			});
		}
	}

	public async signFSA(): Promise<boolean> {
		const SignFSADto = new CorporateFunderSignFsaDTO();
		const result: ResponseDTO<string> = await this.http.request<CorporateFunderSignFsaDTO, string>(SignFSADto);
		if (result.responseKey === HTTP_STATUS_RESPONSE_KEY.SUCCESS) {
			runInAction(() => {
				this.onboardingStore.setFunderOnboardingStatus(ONBOARDING_STATUSES.SignedFSA);
				this.onboardingFSA = undefined;
			});
			return true;
		}
		return false;
	}

	public async submitFsaFile(formData: FormData): Promise<boolean> {
		const rs = await this.http.uploadFile<string>(ENDPOINT.CORPORATE_UPLOAD_FSA_FILE, formData);
		if (rs.responseKey == HTTP_STATUS_RESPONSE_KEY.SUCCESS) {
			return true;
		}
		return false;
	}

	dispose(): void {
		this.companyDetail = {};
		this.bankAccount = {};
		this.contractPerson = {};
		this.corporateLevelDocuments = {};
	}

	get monthsMaximumOption(): IMonthsOption[] {
		const minimumValue = this.loanLendingCriteria?.minLoanPeriod;

		if (minimumValue) {
			const monthsOptionMax = MONTH_OPTIONS.filter((i) => i.value >= minimumValue);

			return monthsOptionMax;
		}
		return [];
	}
	public updateMinimumLoanPeriod(value: number): void {
		this.loanLendingCriteria = {
			...this.loanLendingCriteria,
			minLoanPeriod: value
		};
	}

	public updateMaritalStatus(value: string): void {
		this.contractPerson = {
			...this.contractPerson,
			maritalStatus: value
		};
	}
	public updatePropertyCountry(value: string[]): void {
		this.loanLendingCriteria = {
			...this.loanLendingCriteria,
			propertyCountry: value
		};
	}

	public clearOtherInput(type: string): void {
		if (type === 'otherMaritalStatus') {
			this.contractPerson = {
				...this.contractPerson,
				otherMaritalStatus: undefined
			};
		}

		if (type === 'otherSourceOfFunds') {
			this.contractPerson = {
				...this.contractPerson,
				otherSourceOfFunds: undefined
			};
		}

		if (type === 'titleOther') {
			this.contractPerson = {
				...this.contractPerson,
				titleOther: undefined
			};
		}
	}

	public updateSourceOfFunds(value: string[]): void {
		this.contractPerson = {
			...this.contractPerson,
			sourceOfFunds: value
		};
	}

	public updateContactPerson(newContactPerson: IContactPerson): void {
		this.contractPerson = {
			...this.contractPerson,
			...newContactPerson
		};
	}

	public updateQuestionOtherDetails(value: IQuestionBorrowerOther): void {
		this.onboardingOtherDetail = {
			...this.onboardingOtherDetail,
			questions: {
				...value
			}
		};
	}

	public disableButtonSubmit(step: number): boolean {
		if (step === 2) {
			return false;
		}

		if (this.onboardingStatus === ONBOARDING_STATUSES.Draft) {
			const isUploadSupportingDocumentsForFunderCompany =
				Number(this.supportingDocuments?.identities?.length) > 0 &&
				Number(this.supportingDocuments?.selfie?.length) > 0 &&
				Number(this.supportingDocuments?.proofIncome?.length) > 0 &&
				Number(this.supportingDocuments?.residentialProof?.length) > 0 &&
				Number(this.corporateLevelDocuments?.certificateOfIncorporation?.length) > 0 &&
				Number(this.corporateLevelDocuments?.businessRegistrationCertification?.length) > 0 &&
				Number(this.corporateLevelDocuments?.latestAnnualReturn?.length) > 0 &&
				Number(this.corporateLevelDocuments?.certificateOfIncumbency?.length) > 0 &&
				Number(this.corporateLevelDocuments?.latestAuditedFinancialStatement?.length) > 0 &&
				Number(this.corporateLevelDocuments?.groupOwnership?.length) > 0 &&
				Number(this.corporateLevelDocuments?.signedBoardResolution?.length) > 0;

			if (isUploadSupportingDocumentsForFunderCompany && this.onboardingType === ONBOARDING_TYPE.FUNDER) {
				return false;
			}

			const isUploadSupportingDocumentsForBorrowerCompany =
				Number(this.corporateLevelDocuments?.certificateOfIncorporation?.length) > 0 &&
				Number(this.corporateLevelDocuments?.businessRegistrationCertification?.length) > 0 &&
				Number(this.corporateLevelDocuments?.latestAnnualReturn?.length) > 0 &&
				Number(this.corporateLevelDocuments?.groupOwnership?.length) > 0 &&
				Number(this.corporateLevelDocuments?.signedBoardResolution?.length) > 0 &&
				Number(this.corporateLevelDocuments?.latestAuditedFinancialStatement?.length) > 0;

			if (isUploadSupportingDocumentsForBorrowerCompany && this.onboardingType === ONBOARDING_TYPE.BORROWER) {
				return false;
			}
			return true;
		}
		return true;
	}

	get disabledSubmitBtn(): boolean {
		const contactPersonNation: string | undefined = this.contractPerson?.nationality;

		const isAlwayFunderFileUploadedEnought = (this.supportingDocuments &&
			(this.supportingDocuments.selfie || []).length > 0 &&
			(this.supportingDocuments.residentialProof || []).length > 0 &&
			(this.supportingDocuments.proofIncome || []).length > 0) as boolean;

		const isRequiredFunderFileByNationality = (
			contactPersonNation === CHINA_NATIONALITY_VALUE
				? this.supportingDocuments &&
				(this.supportingDocuments.chineseIdImgFront || []).length > 0 &&
				(this.supportingDocuments.chineseIdImgBack || []).length > 0 &&
				(this.supportingDocuments.bankCardImg || []).length > 0
				: this.supportingDocuments && (this.supportingDocuments.identities || []).length > 0
		) as boolean;

		const isUploadRequiredDocs =
			this.corporateLevelDocuments &&
			this.corporateLevelDocuments.certificateOfIncorporation &&
			this.corporateLevelDocuments.businessRegistrationCertification &&
			this.corporateLevelDocuments.latestAnnualReturn &&
			this.corporateLevelDocuments.latestAuditedFinancialStatement &&
			this.corporateLevelDocuments.groupOwnership &&
			this.corporateLevelDocuments.signedBoardResolution;

		// const isAllStepCompleted = this.onboardingType === ONBOARDING_TYPE.FUNDER ?
		// 	Object.values(this.funderStepValidattion).every(item => Boolean(item)) :
		// 	Object.values(this.borrowerStepValidation).every(item => Boolean(item))

		if ((
			(this.onboardingType === ONBOARDING_TYPE.FUNDER &&
				isAlwayFunderFileUploadedEnought &&
				isRequiredFunderFileByNationality &&
				this.loanLendingCriteria?.id &&
				this.bankAccount?.id) ||
			(this.onboardingType === ONBOARDING_TYPE.BORROWER &&
				isUploadRequiredDocs &&
				this.companyDetail?.id &&
				this.onboardingOtherDetail?.id)
		))
			return false;
		return true;
	}

	get isAllStepComplete(): boolean {
		return this.onboardingType === ONBOARDING_TYPE.FUNDER ?
			Object.values(this.funderStepValidattion).every(item => Boolean(item)) :
			Object.values(this.borrowerStepValidation).every(item => Boolean(item))
	}

	get isChineseNationality(): boolean {
		return this.contractPerson?.nationality === CHINA_NATIONALITY_VALUE;
	}
}
